import {
  EmptyState,
  Hypertext,
  InfoPanel,
  InfoPanelColor,
  useAppContext
} from "@quortex/front-common"
import { useMatchesParams } from "@quortex/front-common/adapters/react-location"
import { useNavigate } from "@tanstack/react-location"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import urlJoin from "url-join"
import { useWorkspacePath } from "@/hooks/navigation"
import emptySegmentation from "@/images/illustration-empty-segmentation.svg?url"
import { usePlayPoolList } from "@/queries/playPools"
import {
  useAcknowledgeNotifications,
  useWorkspaceNotifications
} from "@/queries/workspace-notification"
import { useWorkspaceList } from "@/queries/workspaces"
import { SegmentBalancingVariant, useSegmentWizardStore } from "@/stores/segmentWizard"
import type { AddSegmentModalGenerics } from "../SegmentEdition/AddSegmentModal"

const EmptySegmentation = () => {
  const navigate = useNavigate()
  const { setSegmentBalancingMode } = useSegmentWizardStore()
  const workspacePath = useWorkspacePath()
  const { data: workspace } = useSuspenseQuery({ ...useWorkspaceList(), select: d => d.results[0] })
  const { data: playPools } = useSuspenseQuery({ ...usePlayPoolList() })
  const { apps } = useAppContext()
  const storedSearchView = localStorage.getItem("search.view")
  const { orgUuid } = useMatchesParams()

  const [localNotif, setLocalNotif] = useState<api.switchns.PoolChangedNotification | null>(null)

  const workspaceNotificationQuery = useWorkspaceNotifications()
  const { data: notification } = useSuspenseQuery({
    ...workspaceNotificationQuery,
    queryFn: params => workspaceNotificationQuery.queryFn(params).catch(() => null),
    refetchInterval: 5_000
  })

  const { mutateAsync: acknowledgeNotifications } = useMutation(useAcknowledgeNotifications())

  const emptySegmentationCreation = {
    color: "primary" as const,
    label: "Configure root segment",
    onClick: () => {
      setSegmentBalancingMode(SegmentBalancingVariant.PERCENTAGE)

      navigate({
        replace: true,
        search: {
          modal: "add_segment",
          step: "addBalancing",
          view: storedSearchView || undefined
        } as AddSegmentModalGenerics["Search"]
      })
    },
    type: "button" as const
  }

  useEffect(() => {
    if (notification) {
      void acknowledgeNotifications({})
    }
  }, [notification, acknowledgeNotifications])

  useEffect(() => {
    if (notification) {
      setLocalNotif(notification)
    }
  }, [notification])

  return (
    <div data-testid="empty-segmentation">
      <h2 className="typo-2xl-medium mb-lg">Segmentation</h2>
      {localNotif != null && (
        <InfoPanel
          color={InfoPanelColor.WARNING}
          data-testid="play-pool-changed-panel"
          inline
          links={[
            {
              children: "Go to Play",
              external: true,
              href: urlJoin(
                apps?.play || "",
                "/organizations/",
                orgUuid || "",
                "/play/settings/content-steering"
              )
            }
          ]}
        >
          Pool changed to <strong>{localNotif?.newPoolName}</strong>
        </InfoPanel>
      )}
      <div className="flex flex-col items-center justify-center">
        {playPools.length > 0 && workspace?.originServer === "switch" && (
          <InfoPanel
            className="w-full"
            color="info"
            data-testid="connect-play-switch-panel"
            title="Connect Play to Switch"
          >
            <div>
              You&lsquo;ve configured a pool in Play. You can now connect Play to reuse your
              existing CDNs and avoid duplicate setup.
            </div>
            <Hypertext
              className="mt-xs"
              data-testid="go-to-settings-link"
              size="small"
              to={`${workspacePath}/settings`}
            >
              Go to settings
            </Hypertext>
          </InfoPanel>
        )}
        <EmptyState
          description={
            <>
              <h2 className="typo-xl-bold mb-sm text-center text-black">Configure root segment</h2>
              <div className="text-center">
                Start by setting up the root segment, which includes all viewers. <br />
                You can make viewer segments more specific later on.
              </div>
            </>
          }
          img={emptySegmentation}
          primaryButtonProp={emptySegmentationCreation}
        />
      </div>
    </div>
  )
}

export default EmptySegmentation
